import { UserListRow, UserRbacRole } from '../../api/dto/user-invitation.dto'
import type { GrantBuckets } from './grantBuckets'
import { getDisplayableRbacRoles } from './userDisplay'

/** Company names for a row, preferring the server-resolved display names. */
export const companyLabels = (row: UserListRow): string[] =>
  row.billingClientPublicKeys.map(
    (key, index) => row.billingClientNames[index] ?? key
  )

const roleNames = (roles: UserRbacRole[]): string =>
  getDisplayableRbacRoles(roles)
    .map((role) => role.name)
    .join(', ')

/**
 * A row's companies as a count. Some managers administer thirty of them, and
 * spelling those out buried the status the operator is here to check, so the
 * names stay in the hover title.
 */
const companySummary = (row: UserListRow): { text: string; title: string } => {
  const names = companyLabels(row)
  return {
    text: `${names.length} ${names.length === 1 ? 'company' : 'companies'}`,
    title: names.join(', '),
  }
}

export interface GrantSectionModel {
  key: string
  /** The action, in the imperative: this is what submitting does. */
  title: string
  /** How the action counts in the one-line plan, e.g. "41 to invite". */
  summaryLabel: string
  /** Why these rows get that action. One line. */
  hint: string
  rows: { row: UserListRow; detail: { text: string; title: string } }[]
}

/**
 * The four outcomes, each read the same way: the action heads the section, and
 * every row carries the account's current status beside it.
 */
export const buildSections = (buckets: GrantBuckets): GrantSectionModel[] => {
  const withCompanies = (rows: UserListRow[]) =>
    rows.map((row) => ({ row, detail: companySummary(row) }))

  return [
    {
      key: 'invite',
      title: 'Invite',
      summaryLabel: 'to invite',
      hint: 'No account yet, so they get an activation email',
      rows: withCompanies(buckets.invite),
    },
    {
      key: 'resend',
      title: 'Invite again',
      summaryLabel: 'to invite again',
      hint: 'Their unused link stops working and a new email goes out',
      rows: withCompanies(buckets.resend),
    },
    {
      key: 'assign',
      title: 'Assign the role',
      summaryLabel: 'to get the role',
      hint: 'Already registered, so the role applies at once and no email is sent',
      rows: buckets.assign.map((row) => {
        const held = roleNames(row.rbacRoles ?? [])
        return {
          row,
          detail: {
            text: held.length > 0 ? `Holds ${held}` : 'Holds no role yet',
            title: held,
          },
        }
      }),
    },
    {
      key: 'skip',
      title: 'Skip',
      summaryLabel: 'to skip',
      hint: 'Nothing for this run to do',
      rows: buckets.skip.map(({ row, reason }) => ({
        row,
        detail: { text: reason, title: '' },
      })),
    },
  ].filter((section) => section.rows.length > 0)
}
